import { type InputType } from 'config'
import { accountTypeWithBorrower as _accountTypeWithBorrower } from 'config/user.type.constants'
import { Appraisal2StatusType } from 'pages/Loan/OrderAppraisals/types'
import { loanTypeMap, partiesMapWitBorrower } from 'reducers/loanDetail.reducer'

export const defaultInputs = (): Record<string, InputType> => {
  return {
    loanSection: {
      inputType: 'section',
      title: 'Loan',
      span: 'full',
    },
    minProposedLoanAmount: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Min Proposed Loan Amount',
      value: '',
      required: true,
    },
    maxProposedLoanAmount: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Max Proposed Loan Amount',
      value: '',
      required: true,
    },
    minAsIsValue: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Min As Is Appraised Value',
      value: '',
      required: true,
    },
    minAfterRepairValue: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Profitability Requirement',
      value: '',
      required: true,
    },
    minTotalConstructionFinanced: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Total Construction Financed',
      value: '',
      required: true,
    },
    maxTotalConstructionFinanced: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Total Construction Financed',
      value: '',
      required: true,
    },
    minConstructionFinancedHoldback: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Construction Financed Holdback',
      value: '',
      required: true,
    },
    maxConstructionFinancedHoldback: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Construction Financed Holdback',
      value: '',
      required: true,
    },
    maxPercentOfEstimatedClosingAmountToBorrower: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Percent of Estimated Closing Amount to Borrower',
      value: '',
      required: true,
    },
    minCreditScore: {
      inputType: 'text',
      type: 'number',
      title: 'Min Credit Score',
      value: '',
      required: true,
    },
    maxMultipleProperties: {
      inputType: 'text',
      type: 'number',
      title: 'Max Additional Properties',
      value: '',
      required: true,
    },
    commitmentFeeRequiredTypes: {
      inputType: 'text',
      type: 'text',
      title: 'Min/Max Commitment Fee Amount',
      value: '',
      readOnly: true,
    },
    dueDilligenceRequiredTypes: {
      inputType: 'text',
      type: 'text',
      title: 'Min/Max Due Dilligence Amount',
      value: '',
      readOnly: true,
    },
    minBrokerFeePercent: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Broker Fee Percent',
      value: '',
      required: true,
    },
    maxBrokerFeePercent: {
      inputType: 'text',
      type: 'number',
      title: 'Max Broker Fee Percent',
      value: '',
      prefix: '%',
      required: true,
    },
    minBrokerFeeAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Min Broker Fee Amount',
      value: '',
      prefix: '$',
      required: true,
    },
    defaultUnderwritingFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Default Underwriting Fee',
      value: '',
      prefix: '$',
      required: true,
    },
    rateSheetTime: {
      inputType: 'text',
      type: 'number',
      title: 'Rate Sheet Time',
      value: '',
      prefix: 's',
      required: true,
    },
    priceDisplay: {
      inputType: 'select',
      title: 'Price Display',
      options: ['Price', 'Points'],
      hasDefaultOption: true,
      required: true,
    },
    defaultChannel: {
      inputType: 'select',
      title: 'Default Channel',
      options: loanTypeMap,
      required: true,
    },
    appraisalSection: {
      inputType: 'section',
      title: 'Appraisal',
      span: 'full',
    },
    appraisalStatisticsVisible: {
      inputType: 'multiselect',
      title: 'Visible Appraisal Status',
      options: Appraisal2StatusType,
      defaultSelected: false,
      value: {},
      required: true,
    },
  }
}

export const defaultProductInputs = (): Record<string, InputType> => {
  return {
    productName: {
      inputType: 'text',
      type: 'text',
      title: 'Default Product Name',
      value: '',
      required: true,
    },
    lockTerm: {
      inputType: 'text',
      type: 'number',
      title: 'Default Lock Days',
      value: '',
      required: true,
    },
  }
}

export const defaultLockExtensionInputs = (): Record<string, InputType> => {
  return {
    maxExtensionDays: {
      inputType: 'text',
      type: 'number',
      title: 'Max Extension Combined Days',
      value: '',
      required: true,
    },
    maxExtensionCount: {
      inputType: 'text',
      type: 'number',
      title: 'Max Extension Count',
      value: '',
      required: true,
    },
  }
}

export const defaultProgramInputs = (): Record<string, InputType> => {
  return {
    section: {
      inputType: 'section',
      title: 'Program',
      span: 'full',
    },
    id: {
      inputType: 'text',
      type: 'number',
      title: 'ID',
      value: '',
    },
    programName: {
      inputType: 'text',
      type: 'text',
      title: 'Default Program Name',
      value: '',
      required: true,
    },
    rate: {
      inputType: 'text',
      type: 'number',
      title: 'Default Rate',
      value: '',
      required: true,
    },
    price: {
      inputType: 'text',
      type: 'number',
      title: 'Default Price',
      value: '',
      required: true,
    },
    rateSpread: {
      inputType: 'text',
      type: 'number',
      title: 'Default Rate Spread',
      value: '',
      required: true,
    },
    exitFee: {
      inputType: 'text',
      type: 'number',
      title: 'Default Exit Fee',
      value: '',
      required: true,
    },
  }
}

export const defaultInputsV2 = (partyOptions: any): Record<string, InputType> => {
  const accountTypeWithBorrower = _accountTypeWithBorrower()

  return {
    overviewSection: {
      inputType: 'section',
      title: 'Loan Overview Banner',
      span: 'full',
    },
    overviewFieldsOrder: {
      inputType: 'custom',
      title: 'Field Order',
      span: 'full',
      render: () => {},
    },
    loanNumber: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Loan Number',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    entityName: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Entity Name',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    borrowerName: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Borrower Name',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    propertyAddress: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Property Address',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    loanProgram: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Loan Program',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    mers: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'MERS',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    closingDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Closing Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },

    productSection: {
      inputType: 'section',
      title: 'Pricing Product Display',
      span: 'full',
    },
    productType: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Type',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productProgram: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Program',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productPrice: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Price / Points',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRateDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productLockDays: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Lock Days / Expiration Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRateSpread: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate Spread',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productExitFee: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Exit Fee',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    mfaEnabled: {
      inputType: 'togglebutton',
      title: 'Enable Multi-factor Authentication',
    },
    jwtTokenExpiresIn: {
      inputType: 'text',
      type: 'number',
      title: 'Site Login Time Out in (Days)',
      required: true,
    },
    requirePwChangeIn: {
      inputType: 'text',
      type: 'number',
      title: 'Require Password Changes in (Days)',
      required: true,
    },
    partySection: {
      inputType: 'section',
      title: 'Parties',
      span: 'full',
    },
    partyView: {
      inputType: 'multiselect',
      options: partyOptions,
      title: 'Profile Party View',
      span: 'full',
    },
    partyInfo: {
      inputType: 'select',
      options: { email: 'Email', name: 'Name', companyName: 'Company Name' },
      title: 'Party Information Display',
    },
    pullThroughRatioSection: {
      inputType: 'section',
      title: 'Pull Through Ratio',
      span: 'full',
    },
    pullThroughRatioView: {
      inputType: 'multiselect',
      options: partiesMapWitBorrower,
      title: 'Pull Through Ratio Party View',
      defaultSelected: false,
    },
    hidePullThroughRatio: {
      inputType: 'togglebutton',
      title: 'Hide Pull Through Ratio',
    },
    pullThroughRatioKeys: {
      inputType: 'custom',
      title: 'Pull Through Ratio Party Fields',
      span: 'full',
      render: () => {},
    },
    lei: {
      inputType: 'text',
      title: 'Legal Entity Identifier',
    },
    pipelineSection: {
      inputType: 'section',
      title: 'Pipeline',
      span: 'full',
    },
    defaultPipeline: {
      inputType: 'select',
      options: ['Default', 'Servicing', 'FCI'],
      title: 'Default Pipeline',
    },
  }
}

export const pipelineViewKeys: Record<string, Record<string, string>> = {
  Default: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    asOf: 'As Of',
    noteDate: 'Notes Date',
    proposedLoanAmount: 'Loan Amount',
    lockExp: 'Lock Exp',
    party: 'Account Executive / Broker / Creator',
    underwriterName: 'U.W',
    type: 'Channel',
  },
  Servicing: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    asOf: 'As Of',
    noteDate: 'Notes Date',
    proposedLoanAmount: 'Loan Amount',
    investorCode: 'Investor',
    lastDrawIssueDate: 'Last Draw Issue Date',
    maturityDate: 'Maturity Date',
    type: 'Channel',
  },
  FCI: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    originalBalance: 'Original Balance',
    currentBalance: 'Current Balance',
    daysLate: 'Days Late',
    nextDueDate: 'Next Due Date',
    maturityDate: 'Maturity Date',
    noteRate: 'Note Rate',
    totalPayment: 'Total Payment',
    defaultInterestRate: 'Default Interest Rate',
    originationDate: 'Origination Date',
  },
}
